import { useState } from "react";
import { motion } from "framer-motion";
import { Calendar, CheckCircle2, Clock, Trash2, ChevronRight, Loader2 } from "lucide-react";
import { useGroceryHistory, HistoryItem } from "@/hooks/useGroceryHistory";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";

interface HistoryViewProps {
  isGuest: boolean;
  userId?: string;
  onViewList?: (historyItem: HistoryItem) => void;
}

export const HistoryView = ({ isGuest, userId, onViewList }: HistoryViewProps) => {
  const { history, isLoading, isDeleting, deleteHistoryItem, clearAllHistory } = useGroceryHistory(
    isGuest ? undefined : userId
  );
  const [isClearingAll, setIsClearingAll] = useState(false);

  const handleDelete = async (listId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering the card click
    const success = await deleteHistoryItem(listId);
    if (success) {
      toast({ title: "List Deleted", description: "The grocery list has been removed from your history." });
    } else {
      toast({ title: "Delete Failed", description: "Could not delete the list. Please try again.", variant: "destructive" });
    }
  };

  const handleClearAll = async () => {
    setIsClearingAll(true);
    const success = await clearAllHistory();
    setIsClearingAll(false);
    if (success) {
      toast({ title: "History Cleared", description: "All grocery lists have been removed from your history." });
    } else {
      toast({ title: "Clear Failed", description: "Could not clear history. Please try again.", variant: "destructive" });
    }
  };

  const handleViewList = (item: HistoryItem) => {
    if (onViewList) {
      onViewList(item);
    }
  };

  const getGradeColor = (score: number) => {
    if (score >= 90) return "text-metabolic-500";
    if (score >= 75) return "text-metabolic-400";
    if (score >= 60) return "text-yellow-500";
    return "text-muted-foreground";
  };

  const getGradeBg = (score: number) => {
    if (score >= 90) return "bg-metabolic-500/10";
    if (score >= 75) return "bg-metabolic-400/10";
    if (score >= 60) return "bg-yellow-500/10";
    return "bg-muted/50";
  };

  if (isLoading) {
    return (
      <div className="px-6 py-8 pb-32">
        <h1 className="text-2xl font-semibold text-foreground mb-2">History</h1>
        <p className="text-sm text-muted-foreground mb-8">Your past grocery lists</p>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-2xl border border-border p-4 animate-pulse">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="h-5 w-32 bg-muted rounded mb-2" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
                <div className="h-6 w-16 bg-muted rounded-full" />
              </div>
              <div className="h-2 bg-muted rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="px-6 py-8 pb-32"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground mb-2">History</h1>
          <p className="text-sm text-muted-foreground">
            Your past grocery lists
          </p>
        </div>
        
        {/* Clear All button for authenticated users with history */}
        {!isGuest && history.length > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="outline" 
                size="sm"
                className="text-destructive border-destructive/30 hover:bg-destructive/10"
                disabled={isClearingAll}
              >
                {isClearingAll ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Clear All
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all history?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all {history.length} grocery lists from your history. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearAll}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Clear All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      {history.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center mb-4">
            <Clock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-medium text-foreground mb-2">No history yet</h3>
          <p className="text-sm text-muted-foreground text-center max-w-xs">
            {isGuest
              ? "Guest history resets on refresh. Sign in to save your lists."
              : "Generate your first Metabolic grocery list to see it here."}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {history.map((item, index) => {
            const isComplete = item.completed === item.total;
            const percentage = Math.round((item.completed / item.total) * 100);
            const isCurrentlyDeleting = isDeleting === item.id;

            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleViewList(item)}
                className="bg-card rounded-2xl border border-border p-4 cursor-pointer hover:border-metabolic-400/50 hover:shadow-sm transition-all group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-foreground">{item.goalName}</h3>
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Calendar className="w-3.5 h-3.5" />
                        {item.date}
                      </div>
                      <span className="text-muted-foreground/50">•</span>
                      <span className="text-sm text-muted-foreground">
                        {item.total} items
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {/* Delete Button */}
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          disabled={isCurrentlyDeleting}
                          className="w-8 h-8 rounded-lg bg-secondary/50 flex items-center justify-center hover:bg-destructive/10 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                        >
                          {isCurrentlyDeleting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this list?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently remove "{item.goalName}" list from {item.date} from your history.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={(e) => handleDelete(item.id, e)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${percentage}%` }}
                      transition={{ delay: index * 0.05 + 0.2 }}
                      className={`h-full rounded-full ${
                        isComplete ? "gradient-primary" : "bg-metabolic-400"
                      }`}
                    />
                  </div>
                  <div className={`flex items-center gap-1 text-sm font-medium ${
                    isComplete ? "text-metabolic-500" : "text-muted-foreground"
                  }`}>
                    {isComplete && <CheckCircle2 className="w-4 h-4" />}
                    <span>{item.completed}/{item.total}</span>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
};
